import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalProps } from 'src/modals/Modal';
import {
  addDeepLinkUrlPart,
  useRemoveDeepLinkUrlPart,
} from 'src/utils/deepLinkUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { ActivityLogClient } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useClientLogContext } from '../ClientLogContext';

type ModalContextType = {
  isModalOpen: boolean;
  currentModalConfig: Omit<ModalProps, 'fullscreen'> | null;
  closeModal: (forceClose?: boolean | React.SyntheticEvent) => void;
  setModal: (props: Omit<ModalProps, 'fullscreen'>) => void;
  updateModal: (props: Partial<Omit<ModalProps, 'fullscreen'>>) => void;
};

export const ModalContext = createContext<ModalContextType>({
  isModalOpen: false,
  currentModalConfig: null,
  setModal: () => {},
  updateModal: () => {},
  closeModal: () => {},
});

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalProps, setModalProps] = useState<Omit<
    ModalProps,
    'fullscreen'
  > | null>(null);
  const { deepLinkKey, deepLinkValue } = modalProps ?? {};
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const removeDeepLinkUrlPart = useRemoveDeepLinkUrlPart();
  const navigate = useNavigate();

  // Track the previous modal's deeplink key for removal
  const prevDeepLinkKeyRef = useRef<string | undefined>();

  useEffect(() => {
    if (deepLinkKey && deepLinkValue) {
      const keysToRemove =
        prevDeepLinkKeyRef.current && prevDeepLinkKeyRef.current !== deepLinkKey
          ? [prevDeepLinkKeyRef.current]
          : null;

      addDeepLinkUrlPart(deepLinkKey, deepLinkValue, navigate, keysToRemove);
      prevDeepLinkKeyRef.current = deepLinkKey;
    } else if (!deepLinkKey) {
      // Modal was closed, clear the previous key
      prevDeepLinkKeyRef.current = undefined;
    }
  }, [deepLinkKey, deepLinkValue, navigate]);

  const handleCloseModal = (forceClose?: boolean | React.SyntheticEvent) => {
    if (forceClose !== true && modalProps?.onCloseOverride) {
      modalProps.onCloseOverride();
    } else {
      closeModal();
    }
  };

  const setModal = useCallback(
    (modal: Omit<ModalProps, 'fullscreen'>) => {
      if (modal.deepLinkValue && modal.trackViewEntityType) {
        tryInvokeApi(
          async () => {
            await new ActivityLogClient(
              activeAccountWebClientConfig
            ).trackEntityView(modal.deepLinkValue, modal.trackViewEntityType);
          },
          (error) => {
            trackError('ActivityLogClient.trackEntityView', error, {
              deepLinkValue: modal.deepLinkValue,
              trackViewEntityType: modal.trackViewEntityType,
            });
          }
        );
      }
      setModalProps(modal);
    },
    [activeAccountWebClientConfig, trackError]
  );

  const closeModal = useCallback(() => {
    // TODO: refactor deeplink to a hook and pass as onClose where used
    removeDeepLinkUrlPart(deepLinkKey);
    setModalProps(null);
    prevDeepLinkKeyRef.current = undefined;
  }, [deepLinkKey, removeDeepLinkUrlPart]);

  const updateModal = useCallback<ModalContextType['updateModal']>(
    (partial) => {
      if (modalProps) {
        setModalProps({ ...modalProps, ...partial });
      }
    },
    [modalProps]
  );

  const contextValue = useMemo(
    () => ({
      isModalOpen: !!modalProps,
      currentModalConfig: modalProps,
      setModal: setModal,
      updateModal,
      closeModal: handleCloseModal,
    }),
    [handleCloseModal, modalProps, setModal, updateModal]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalProps && (
        <Modal
          {...modalProps}
          isOpen
          onClose={handleCloseModal}
          onExit={modalProps?.onExit}
        />
      )}
    </ModalContext.Provider>
  );
};
